import React, { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import type { ExistingSurveyFile } from 'src/redux/slices/Interview/types';
import { getSurveyFileFromRenterFile } from 'src/services/interviewSurveyFiles';

interface Props {
  file: ExistingSurveyFile;
  onBack: () => void;
  onDelete: (file: ExistingSurveyFile) => Promise<void>;
}

function formatUploadedAt(isoString: string | null | undefined): string | null {
  if (!isoString) {
    return null;
  }
  try {
    return new Date(isoString).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  } catch {
    return null;
  }
}

export default function SurveyFileDetailView({
  file,
  onBack,
  onDelete,
}: Props): React.ReactElement {
  const [removing, setRemoving] = useState(false);
  const [removeError, setRemoveError] = useState<string | null>(null);
  const [resolvedFile, setResolvedFile] = useState<ExistingSurveyFile>(file);
  const [resolving, setResolving] = useState(false);

  // Lazy fallback: if the file was constructed from upload-context placeholders
  // (no pdf_file_url), try to resolve it to the real SurveyFile data via the
  // RenterFile → SurveyFile bridge endpoint.
  useEffect(() => {
    if (file.pdf_file_url || file.file_id <= 0 || resolving) {
      return;
    }
    let cancelled = false;
    setResolving(true);
    getSurveyFileFromRenterFile(file.file_id)
      .then((sf) => {
        if (!cancelled) {
          setResolvedFile(sf);
        }
      })
      .catch(() => {
        // Bridge failed — keep showing the placeholder
      })
      .finally(() => {
        if (!cancelled) {
          setResolving(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [file.file_id, file.pdf_file_url, resolving]);

  const handleRemove = async (): Promise<void> => {
    setRemoving(true);
    setRemoveError(null);
    try {
      await onDelete(file);
      onBack();
    } catch {
      setRemoveError('Could not remove this file. Please try again.');
      setRemoving(false);
    }
  };

  const uploadedAt = formatUploadedAt(resolvedFile.created_at);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        bgcolor: 'background.paper',
      }}
      data-testid="survey-file-detail"
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 2,
          py: 1.5,
          bgcolor: 'grey.900',
          flexShrink: 0,
        }}
      >
        <Button
          sx={{
            color: 'grey.400',
            textTransform: 'none',
            fontSize: 14,
            fontWeight: 500,
            px: 0,
            minWidth: 0,
            '&:hover': { color: 'grey.100', backgroundColor: 'transparent' },
          }}
          onClick={onBack}
          startIcon={<ArrowBackIcon sx={{ fontSize: '18px !important' }} />}
          disableRipple
          aria-label="Back to file uploads"
          data-testid="back-to-hub-btn"
        >
          <span>Back to file uploads</span>
        </Button>
      </Box>

      {resolvedFile.pdf_file_url ? (
        <Box
          sx={{
            flex: 1,
            minHeight: 400,
            bgcolor: 'grey.900',
          }}
        >
          <iframe
            src={resolvedFile.pdf_file_url}
            title={resolvedFile.file_name}
            style={{ width: '100%', height: '100%', border: 'none' }}
          />
        </Box>
      ) : resolving ? (
        <Box
          sx={{
            height: 200,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'grey.100',
          }}
        >
          <CircularProgress size={32} />
        </Box>
      ) : (
        <Box
          sx={{
            height: 200,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'grey.100',
          }}
        >
          <Typography sx={{ color: 'text.secondary' }}>
            Preview not available
          </Typography>
        </Box>
      )}

      <Box sx={{ flexShrink: 0, px: 2, pt: 2.5, pb: 4 }}>
        <Typography sx={{ fontSize: 16, fontWeight: 600, mb: 0.5 }}>
          {resolvedFile.file_name}
        </Typography>
        {uploadedAt && (
          <Typography sx={{ fontSize: 13, color: 'text.secondary', mb: 2.5 }}>
            Uploaded {uploadedAt}
          </Typography>
        )}
        {!uploadedAt && (
          <Typography sx={{ fontSize: 13, color: 'text.secondary', mb: 2.5 }}>
            Previously uploaded document
          </Typography>
        )}

        {resolvedFile.is_rejected ? (
          <Typography
            sx={{ fontSize: 14, fontWeight: 600, color: 'warning.dark' }}
          >
            Rejected — please re-upload
          </Typography>
        ) : resolvedFile.status === 1 ? (
          <Typography
            sx={{ fontSize: 14, fontWeight: 500, color: 'success.main' }}
          >
            Approved
          </Typography>
        ) : (
          <>
            <Button
              variant="outlined"
              color="error"
              onClick={handleRemove}
              disabled={removing}
              fullWidth
              data-testid="remove-survey-file-btn"
              startIcon={
                removing ? (
                  <CircularProgress size={14} color="inherit" />
                ) : undefined
              }
            >
              <span>{removing ? 'Removing\u2026' : 'Remove file'}</span>
            </Button>
            {removeError && (
              <Typography sx={{ fontSize: 13, color: 'error.main', mt: 0.5 }}>
                {removeError}
              </Typography>
            )}
          </>
        )}
      </Box>
    </Box>
  );
}
